import { flow, types } from 'mobx-state-tree';
import getAppRoot from './utils/getAppRoot';
import OrganizationMetadataQuery from './queries/organization/OrganizationMetadataQuery';
import { GenerateOrganizationLogoUploadUrlArgs, UpdateOrganizationBrandingDto, UpdateOrganizationSettingsDto, UpdateOrganizationProfileDto } from './models/RootStore.base';
import {
  DateTimeFormat,
  DistanceUnit,
  OrganizationSettingsDtoModel,
  selectFromOrganizationLogoUploadUrlDto,
} from './models';
import { MAX_ORGANIZATION_LOGO_SIZE_BYTES } from '../../constants';

const Organization = types.model('Organization')
  .props({
    name: types.maybe(types.string),
    address: types.maybeNull(types.string),
    phoneNumber: types.maybeNull(types.string),
    customerFacingEmail: types.maybeNull(types.string),
    website: types.maybeNull(types.string),
    logoUrl: types.maybeNull(types.string),
    brandColor: types.maybeNull(types.string),
    settings: types.optional(OrganizationSettingsDtoModel, {}),
  })
  .views((self) => ({
    get getDistanceUnit() {
      const { distanceUnit } = self.settings;
      return distanceUnit;
    },
  }))
  .actions((self) => ({
    setDistanceUnit(distanceUnit: DistanceUnit) {
      self.settings.distanceUnit = distanceUnit;
    },
    getOrganizationMetadata: flow(function* () {
      const res = yield getAppRoot(self).model.queryOrganizations({}, OrganizationMetadataQuery, { fetchPolicy: 'no-cache' }  ).currentPromise();
      const {
        name, address, phoneNumber, customerFacingEmail, website, logoUrl, brandColor, settings,
      } = res.organizations;
      self.name = name;
      self.address = address;
      self.phoneNumber = phoneNumber;
      self.customerFacingEmail = customerFacingEmail;
      self.website = website;
      self.logoUrl = logoUrl;
      self.brandColor = brandColor;
      self.settings = { ...settings };
      if (!self.settings?.distanceUnit) self.settings.distanceUnit = DistanceUnit.KM;
      if (!self.settings?.dateTimeFormat) self.settings.dateTimeFormat = DateTimeFormat.MILITARY;
    }),
    updateOrganizationSettings: flow(function* (changes: UpdateOrganizationSettingsDto) {
      yield getAppRoot(self).model.mutatePatchOrganizationSettings(
        { changes },
        qb => qb.distanceUnit.dateTimeFormat.dateFormatPreference.allowDriverOutOfSequence,
        () => {
          self.settings = {
            ...self.settings,
            dateTimeFormat: changes.dateTimeFormat != null ? changes.dateTimeFormat : self.settings.dateTimeFormat ?? undefined,
            distanceUnit: changes.distanceUnit != null ? changes.distanceUnit : self.settings.distanceUnit ?? undefined,
            dateFormatPreference: changes.dateFormatPreference != null ? changes.dateFormatPreference : self.settings.dateFormatPreference ?? undefined,
            allowDriverOutOfSequence: changes.allowDriverOutOfSequence != null ? changes.allowDriverOutOfSequence : self.settings.allowDriverOutOfSequence ?? undefined,
          };
        },
      ).currentPromise();
    }),
    resetCsvMappings: flow(function* () {
      yield getAppRoot(self).model.mutateDeleteCsvMappings().currentPromise();
    }),
    updateOrganizationProfile: flow(function* (changes: UpdateOrganizationProfileDto) {
      yield getAppRoot(self).model.mutatePatchOrganizationProfile(
        { changes },
        () => {
          self.name = changes.name;
        },
      ).currentPromise();
    }),
    updateOrganizationBranding: flow(function* (changes: UpdateOrganizationBrandingDto, options?: { skipLogoUrlOptimisticUpdate?: boolean }) {
      yield getAppRoot(self).model.mutatePatchOrganizationBranding(
        { changes },
        () => {
          if (Object.prototype.hasOwnProperty.call(changes, 'address')) {
            self.address = changes.address ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'phoneNumber')) {
            self.phoneNumber = changes.phoneNumber ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'customerFacingEmail')) {
            self.customerFacingEmail = changes.customerFacingEmail ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'website')) {
            self.website = changes.website ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'logoUrl') && !options?.skipLogoUrlOptimisticUpdate) {
            self.logoUrl = changes.logoUrl ?? null;
          }
          if (Object.prototype.hasOwnProperty.call(changes, 'brandColor')) {
            self.brandColor = changes.brandColor ?? null;
          }
        },
      ).currentPromise();
    }),
  }))
  .actions((self) => ({
    uploadOrganizationLogo: flow(function* (file: File) {
      const input: GenerateOrganizationLogoUploadUrlArgs = {
        contentType: file.type,
        sizeBytes: file.size,
      };

      const res = yield getAppRoot(self).model.mutateGenerateOrganizationLogoUploadUrl(
        { input },
        selectFromOrganizationLogoUploadUrlDto().uploadUrl.readUrl.objectPath.toString(),
      ).currentPromise();

      const { uploadUrl, readUrl, objectPath } = res.generateOrganizationLogoUploadUrl;
      const uploadResponse: Response = yield fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          'x-goog-content-length-range': `0,${MAX_ORGANIZATION_LOGO_SIZE_BYTES}`,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload organization logo');
      }

      self.logoUrl = readUrl;
      yield self.updateOrganizationBranding({ logoUrl: objectPath }, { skipLogoUrlOptimisticUpdate: true });
      return readUrl;
    }),
  }));

export default Organization;
